import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Plus, Edit, Loader2, Trash2, User, Users, Calendar, Award, Star, BookOpen, MessageSquare, AlertCircle, FileText, CheckCircle, ExternalLink } from 'lucide-react';
import { performanceApi, employeeApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';
import { cn } from '../../lib/utils';

interface ReviewFormData {
  employee_id: string;
  review_period_start: string;
  review_period_end: string;
  year: number;
  status: string;
  overall_rating: number | null;
  comments: string;
  employee_self_assessment: string;
  manager_feedback: string;
  manager_rating: number | null;
}

const PerformanceReviews: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'my-reviews' | 'team-reviews'>('my-reviews');
  const { isUserView, canViewTeamReviews, canSelectReviewEmployee, canViewManagerFeedback } = useManagerViewPermission();

  const [formData, setFormData] = useState<ReviewFormData>({
    employee_id: '',
    review_period_start: '',
    review_period_end: '',
    year: new Date().getFullYear(),
    status: 'DRAFT',
    overall_rating: null,
    comments: '',
    employee_self_assessment: '',
    manager_feedback: '',
    manager_rating: null,
  });

  // Determine if user is manager/reviewer
  const isManager = (user?.role === 'MANAGER' || user?.role === 'HR' || user?.role === 'ADMIN') && !isUserView && canViewTeamReviews;
  const currentUserId = user?.id || '';
  const currentUserName = user ? `${user.first_name} ${user.last_name}` : 'Unknown';

  const { data: reviews = [], isLoading, error } = useQuery({
    queryKey: ['performanceReviews', filterStatus, viewMode, currentUserId],
    queryFn: () => {
      const params: any = {
        status: filterStatus !== 'all' ? filterStatus : undefined,
      };
      
      if (viewMode === 'my-reviews') {
        params.employee_id = currentUserId;
      }
      
      return performanceApi.reviews.getAll(params);
    },
  });

  const { data: employeesData = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  const { data: reviewersData = [] } = useQuery({
    queryKey: ['reviewers'],
    queryFn: () => employeeApi.getReviewers(),
  });

  const employees = Array.isArray(employeesData) ? employeesData : [];
  const reviewers = Array.isArray(reviewersData) ? reviewersData : [];
  const reviewsArray = Array.isArray(reviews) ? reviews : [];

  const createMutation = useMutation({
    mutationFn: performanceApi.reviews.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceReviews'] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: 'Review created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => performanceApi.reviews.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceReviews'] });
      setIsDialogOpen(false);
      setEditingReview(null);
      resetForm();
      toast({ title: 'Review updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => performanceApi.reviews.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceReviews'] });
      toast({ title: 'Review deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleDelete = (review: any) => {
    if (window.confirm(`Are you sure you want to delete the review for ${getEmployeeName(review.employee_id)}?`)) {
      deleteMutation.mutate(review.id);
    }
  };

  const handleSubmit = () => {
    if (!formData.employee_id) {
      toast({ title: 'Error', description: 'Please select an employee', variant: 'destructive' });
      return;
    }

    if (formData.overall_rating !== null && (formData.overall_rating < 1 || formData.overall_rating > 5)) {
      toast({ title: 'Error', description: 'Overall Rating must be between 1 and 5', variant: 'destructive' });
      return;
    }

    if (formData.manager_rating !== null && (formData.manager_rating < 1 || formData.manager_rating > 5)) {
      toast({ title: 'Error', description: 'Manager Rating must be between 1 and 5', variant: 'destructive' });
      return;
    }

    if (editingReview) {
      updateMutation.mutate({ id: editingReview.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const openDialog = (review?: any) => {
    if (review) {
      setEditingReview(review);
      setFormData({
        employee_id: review.employee_id,
        review_period_start: review.review_period_start,
        review_period_end: review.review_period_end,
        year: review.year || new Date().getFullYear(),
        status: review.status,
        overall_rating: review.overall_rating,
        comments: review.comments || '',
        employee_self_assessment: review.employee_self_assessment || '',
        manager_feedback: review.manager_feedback || '',
        manager_rating: review.manager_rating,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      employee_id: !isManager ? currentUserId : '',
      review_period_start: '',
      review_period_end: '',
      year: new Date().getFullYear(),
      status: 'DRAFT',
      overall_rating: null,
      comments: '',
      employee_self_assessment: '',
      manager_feedback: '',
      manager_rating: null,
    });
    setEditingReview(null);
  };

  const getEmployeeName = (employeeId: string) => {
    if (employeeId === currentUserId) {
      return currentUserName;
    }
    const emp = employees.find((e: any) => e.id === employeeId);
    if (emp) return `${emp.first_name} ${emp.last_name}`;
    const reviewer = reviewers.find((r: any) => r.id === employeeId);
    if (reviewer) return `${reviewer.first_name} ${reviewer.last_name}`;
    return 'Unknown';
  };

  const getEmployeeInitials = (name: string) => {
    const parts = name.trim().split(' ');
    const first = parts[0]?.charAt(0) || '';
    const last = parts[parts.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      DRAFT: 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-955/20 dark:text-slate-400 dark:border-slate-900/50',
      IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200/50 dark:bg-blue-955/20 dark:text-blue-400 dark:border-blue-900/50',
      COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-955/20 dark:text-emerald-400 dark:border-emerald-900/50',
      APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-955/20 dark:text-emerald-400 dark:border-emerald-900/50',
    };
    const key = status?.toUpperCase() || 'DRAFT';
    const classNames = statusColors[key] || 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-955/20 dark:text-slate-400 dark:border-slate-900/50';
    return (
      <Badge className={cn("px-2.5 py-1 rounded-lg font-bold text-xs tracking-wide shadow-none border", classNames)} variant="outline">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 font-bold text-slate-400">Loading reviews...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="h-5 w-5 shrink-0" />
        <div>
          <p className="font-bold">Error loading reviews</p>
          <p className="text-sm mt-0.5">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header section with inline controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Performance Reviews</h2>
          <p className="text-sm text-slate-500 font-bold tracking-wide mt-1">Manage employee performance reviews</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          {isManager && (
            <div className="flex bg-slate-100 dark:bg-slate-950 p-1 rounded-lg">
              <Button
                variant="ghost"
                onClick={() => setViewMode('my-reviews')}
                className={cn(
                  "h-8 px-4 rounded-md text-xs font-bold transition-all shadow-none",
                  viewMode === 'my-reviews' 
                    ? 'bg-white dark:bg-slate-900 text-[#143A62] dark:text-white shadow-sm' 
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                )}
              >
                <User className="h-3.5 w-3.5 mr-1.5" />
                My Reviews
              </Button>
              <Button
                variant="ghost"
                onClick={() => setViewMode('team-reviews')}
                className={cn(
                  "h-8 px-4 rounded-md text-xs font-bold transition-all shadow-none",
                  viewMode === 'team-reviews' 
                    ? 'bg-white dark:bg-slate-900 text-[#143A62] dark:text-white shadow-sm' 
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                )}
              >
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Team Reviews
              </Button>
            </div>
          )}

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-9 rounded-xl shadow-sm">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="border-slate-100 dark:border-slate-800">
              <SelectItem value="all" className="text-xs font-bold">All Status</SelectItem>
              <SelectItem value="DRAFT" className="text-xs font-bold">Draft</SelectItem>
              <SelectItem value="IN_PROGRESS" className="text-xs font-bold">In Progress</SelectItem>
              <SelectItem value="COMPLETED" className="text-xs font-bold">Completed</SelectItem>
              <SelectItem value="APPROVED" className="text-xs font-bold">Approved</SelectItem>
            </SelectContent>
          </Select>

          <Button 
            onClick={() => openDialog()}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New Review
          </Button>
        </div>
      </div>

      {/* Main Reviews Card */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-xl">
              <Award className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
                {viewMode === 'my-reviews' ? 'My Reviews' : 'Team Reviews to Conduct'}
              </CardTitle>
              <p className="text-[11px] text-slate-400 font-bold tracking-widest mt-0.5">List of performance assessments</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Employee</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Reviewer</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Review Period</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Self Rating</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Manager Rating</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviewsArray.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-20 px-8">
                    <div className="flex flex-col items-center">
                      <div className="bg-slate-50 dark:bg-slate-800 h-16 w-16 rounded-2xl flex items-center justify-center mb-4 border border-slate-100 dark:border-slate-700">
                        <Award className="h-8 w-8 text-slate-300 dark:text-slate-650" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">No reviews found</h3>
                      <p className="text-sm text-slate-400 mt-1">
                        {viewMode === 'my-reviews' 
                          ? 'No reviews found for you. Create your first performance review.'
                          : 'No team reviews currently assigned.'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                reviewsArray.map((review: any) => {
                  const empName = getEmployeeName(review.employee_id);
                  const reviewerName = review.reviewer_id ? getEmployeeName(review.reviewer_id) : '';
                  
                  return (
                    <TableRow key={review.id} className="group border-b border-slate-50 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors cursor-default">
                      <TableCell className="px-8 py-5">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#143A62]/10 to-blue-500/10 text-[#143A62] dark:text-blue-400 font-bold text-xs flex items-center justify-center border border-slate-100 dark:border-slate-800 shrink-0">
                            {getEmployeeInitials(empName)}
                          </div>
                          <div>
                            <div className="font-bold text-[#143A62] dark:text-blue-400 text-sm">{empName}</div>
                            {review.employee_self_assessment && (
                              <div className="text-[11px] text-slate-400 mt-0.5 max-w-xs truncate italic">
                                Self: "{review.employee_self_assessment}"
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-5">
                        {review.reviewer_id ? (
                          <div className="flex items-center gap-2">
                            <div className="h-7 w-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-350 font-bold text-[10px] flex items-center justify-center border border-slate-200/50 dark:border-slate-700 shrink-0">
                              {getEmployeeInitials(reviewerName)}
                            </div>
                            <div>
                              <div className="text-xs font-bold text-slate-700 dark:text-slate-300">{reviewerName}</div>
                              {review.manager_feedback && (
                                <div className="text-[10px] text-slate-400 max-w-[180px] truncate mt-0.5">
                                  Feedback: "{review.manager_feedback}"
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs font-bold text-slate-400 italic">Not reviewed</span>
                        )}
                      </TableCell>
                      <TableCell className="py-5">
                        <div className="text-xs font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                          <span>
                            {new Date(review.review_period_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - {new Date(review.review_period_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-5">
                        {review.overall_rating ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 shrink-0" />
                            <span className="font-black text-slate-800 dark:text-slate-200">{review.overall_rating}</span>
                            <span className="text-slate-400 text-xs">/5</span>
                          </div>
                        ) : (
                          <span className="text-xs font-bold text-slate-400 italic">N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="py-5">
                        {review.manager_rating ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="h-3.5 w-3.5 fill-purple-400 text-purple-400 shrink-0" />
                            <span className="font-black text-slate-800 dark:text-slate-200">{review.manager_rating}</span>
                            <span className="text-slate-400 text-xs">/5</span>
                          </div>
                        ) : (
                          <span className="text-xs font-bold text-slate-400 italic">N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="py-5">{getStatusBadge(review.status)}</TableCell>
                      <TableCell className="px-8 py-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openDialog(review)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(review)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[min(96vw,800px)] max-w-[800px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 transition-colors duration-300 gap-0 overflow-hidden shadow-2xl">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-500" />
              {editingReview ? 'Edit Performance Review' : 'Create Performance Review'}
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Configure employee performance review details
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
            {/* Employee Field */}
            <div className="space-y-1.5">
              <Label htmlFor="employee_id" className="text-xs font-bold text-slate-400 tracking-wider">Employee *</Label>
              {!isManager ? (
                <Input
                  value={currentUserName}
                  disabled
                  className="bg-slate-50 dark:bg-slate-850 rounded-xl border-slate-200 dark:border-slate-800 font-bold text-sm h-11"
                />
              ) : (
                <Select
                  value={formData.employee_id}
                  onValueChange={(val) => setFormData({ ...formData, employee_id: val })}
                >
                  <SelectTrigger 
                    disabled={!!editingReview || (!isManager && !canSelectReviewEmployee)}
                    className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm"
                  >
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-100 dark:border-slate-800">
                    {employees.map((emp: any) => (
                      <SelectItem key={emp.id} value={emp.id} className="text-sm font-bold">
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-[10px] text-slate-400 font-semibold tracking-wide">
                {!isManager 
                  ? 'This review is for your performance'
                  : 'The reviewer will be automatically assigned when a manager adds feedback'}
              </p>
            </div>

            {/* Dates Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="review_period_start" className="text-xs font-bold text-slate-400 tracking-wider">Period Start *</Label>
                <Input
                  id="review_period_start"
                  type="date"
                  value={formData.review_period_start}
                  onChange={(e) => setFormData({ ...formData, review_period_start: e.target.value })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="review_period_end" className="text-xs font-bold text-slate-400 tracking-wider">Period End *</Label>
                <Input
                  id="review_period_end"
                  type="date"
                  value={formData.review_period_end}
                  onChange={(e) => setFormData({ ...formData, review_period_end: e.target.value })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
            </div>

            {/* Year / Status Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="year" className="text-xs font-bold text-slate-400 tracking-wider">Year *</Label>
                <Input
                  id="year"
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) || new Date().getFullYear() })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="status" className="text-xs font-bold text-slate-400 tracking-wider">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-100 dark:border-slate-800">
                    <SelectItem value="DRAFT" className="text-xs font-bold">Draft</SelectItem>
                    <SelectItem value="IN_PROGRESS" className="text-xs font-bold">In Progress</SelectItem>
                    <SelectItem value="COMPLETED" className="text-xs font-bold">Completed</SelectItem>
                    <SelectItem value="APPROVED" className="text-xs font-bold">Approved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Employee Self-Assessment Section */}
            <div className="border-t border-slate-150 dark:border-slate-800/80 pt-5 space-y-4">
              <h3 className="text-sm font-black text-slate-400 tracking-widest flex items-center gap-1.5">
                <BookOpen className="h-4 w-4 text-blue-500" />
                Employee Self-Assessment
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="employee_self_assessment" className="text-xs font-bold text-slate-400 tracking-wider">Self-Assessment Remarks</Label>
                  <Textarea
                    id="employee_self_assessment"
                    value={formData.employee_self_assessment}
                    onChange={(e) => setFormData({ ...formData, employee_self_assessment: e.target.value })}
                    placeholder="Reflect on your performance, achievements, and development goals..."
                    rows={3}
                    disabled={isManager && editingReview && !!editingReview.employee_self_assessment}
                    className={cn(
                      "rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm",
                      isManager && editingReview && editingReview.employee_self_assessment && "bg-slate-50/50 dark:bg-slate-850 cursor-not-allowed"
                    )}
                  />
                  <p className="text-[10px] text-slate-400 font-semibold tracking-wide flex items-center gap-1">
                    {isManager && editingReview && editingReview.employee_self_assessment ? (
                      <>
                        <CheckCircle className="h-3 w-3 text-emerald-500" />
                        <span>Submitted by employee (read-only)</span>
                      </>
                    ) : (
                      "Your assessment of your achievements and learning milestones"
                    )}
                  </p>
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="overall_rating" className="text-xs font-bold text-slate-400 tracking-wider">Self Rating (1-5)</Label>
                  <Input
                    id="overall_rating"
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    value={formData.overall_rating || ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseFloat(e.target.value) : null;
                      if (value === null || (value >= 1 && value <= 5)) {
                        setFormData({ ...formData, overall_rating: value });
                      }
                    }}
                    disabled={isManager && editingReview && editingReview.overall_rating !== null}
                    className={cn(
                      "rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11",
                      isManager && editingReview && editingReview.overall_rating !== null && "bg-slate-50/50 dark:bg-slate-850 cursor-not-allowed"
                    )}
                  />
                  <p className="text-[10px] text-slate-400 font-semibold tracking-wide flex items-center gap-1">
                    {isManager && editingReview && editingReview.overall_rating !== null ? (
                      <>
                        <CheckCircle className="h-3 w-3 text-emerald-500" />
                        <span>Submitted by employee (read-only)</span>
                      </>
                    ) : (
                      "Your rating score from 1.0 to 5.0"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Manager Feedback Section */}
            {!isUserView && canViewManagerFeedback && (
              <div className="border-t border-slate-150 dark:border-slate-800/80 pt-5 space-y-4">
                <h3 className="text-sm font-black text-slate-400 tracking-widest flex items-center gap-1.5">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  Manager Feedback
                </h3>
                
                <div className="grid gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="manager_feedback" className="text-xs font-bold text-slate-400 tracking-wider">Evaluation Comments</Label>
                    <Textarea
                      id="manager_feedback"
                      value={formData.manager_feedback}
                      onChange={(e) => setFormData({ ...formData, manager_feedback: e.target.value })}
                      placeholder="Provide objective professional feedback and key areas of growth..."
                      rows={3}
                      className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="manager_rating" className="text-xs font-bold text-slate-400 tracking-wider">Manager Rating (1-5)</Label>
                    <Input
                      id="manager_rating"
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={formData.manager_rating || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : null;
                        if (value === null || (value >= 1 && value <= 5)) {
                          setFormData({ ...formData, manager_rating: value });
                        }
                      }}
                      className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* General Comments */}
            <div className="border-t border-slate-150 dark:border-slate-800/80 pt-5 space-y-1.5">
              <Label htmlFor="comments" className="text-xs font-bold text-slate-400 tracking-wider">General Comments</Label>
              <Textarea
                id="comments"
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="Additional administrative notes or remarks..."
                rows={2}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
              />
            </div>
          </div>
          
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingReview ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformanceReviews;